import {
  freezeCard,
  getCardLimit,
  getCardSecrets,
  getCardStatus,
  getUserCards,
  updateLimit,
  updatePin,
} from "../api/api";
import { useSDKConfigStore } from "../stores/sdkConfigStore";
import { useCardStatusStore } from "../stores/cardStatusStore";
import { encryptPinWithSessionKey } from "../api/utils";
import { CardLimitFrequency } from "../api/types";

/* -------------------------------------------------------------------------- */
/*                              CARD FREEZE LOGIC                             */
/* -------------------------------------------------------------------------- */

/**
 * Freeze or unfreeze a user's card.
 *
 * - Validates wallet & card data from store.
 * - Calls backend to freeze/unfreeze card.
 * - On success, refreshes card status and user card data.
 *
 * @param isFreezing - `true` to freeze, `false` to unfreeze.
 * @returns `{ success, message }` describing result.
 */
export const handleFreezeCard = async ({
  isFreezing,
}: {
  isFreezing: boolean;
}): Promise<{ success: boolean; message: string }> => {
  try {
    console.log(
      `[handleFreezeCard] Initiating ${
        isFreezing ? "freeze" : "unfreeze"
      } process...`
    );

    const { userCard, _setCardStatus } = useCardStatusStore.getState();

    if (!userCard) {
      console.warn("[handleFreezeCard] Missing card data.");
      return {
        success: false,
        message: "Card data is missing.",
      };
    }

    const { cardID } = userCard;
    const actionVerb = isFreezing ? "freeze" : "unfreeze";
    const actionPast = isFreezing ? "frozen" : "unfrozen";

    const { success, message } = await freezeCard(cardID, isFreezing);

    if (success) {
      console.log(
        `[handleFreezeCard] Card successfully ${actionPast}. Refreshing data...`
      );
      const [statusRes, cardsRes] = await Promise.all([
        getCardStatus(),
        getUserCards(),
      ]);
      _setCardStatus(statusRes.data, cardsRes.data?.[0] || null);
    } else {
      console.warn(
        `[handleFreezeCard] Failed to ${actionVerb} card: ${message}`
      );
    }

    return { success, message: message || "" };
  } catch (err) {
    const msg =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error(
      `[handleFreezeCard] Error during ${isFreezing ? "freeze" : "unfreeze"}:`,
      err
    );
    return { success: false, message: msg };
  }
};

/* -------------------------------------------------------------------------- */
/*                           CARD SECRETS RETRIEVAL                           */
/* -------------------------------------------------------------------------- */

/**
 * Fetch encrypted card details (PAN, CVC, Expiry) from backend.
 *
 * - Requires wallet & card data from store.
 * - Uses session authentication + EIP-712 signing.
 *
 * @returns `{ success, message, secrets? }`
 */
export const handleShowCardDetails = async (
  sessionID: string
): Promise<{
  success: boolean;
  message: string;
  secrets?: {
    encryptedPan: { iv: string; data: string };
    encryptedCvc: { iv: string; data: string };
    ExpirationMonth: string;
    ExpirationYear: string;
  };
}> => {
  try {
    console.log("[handleShowCardDetails] Retrieving encrypted card secrets...");

    const { userCard } = useCardStatusStore.getState();

    if (!userCard?.cardID) {
      console.warn("[handleShowCardDetails] Missing card data.");
      return {
        success: false,
        message: "Card ID is missing.",
      };
    }

    const secrets = await getCardSecrets(userCard.cardID, sessionID);
    console.log("[handleShowCardDetails] Secrets fetched successfully.");

    return {
      success: true,
      message: "Card secrets fetched successfully.",
      secrets,
    };
  } catch (err) {
    const msg =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error("[handleShowCardDetails] Failed to fetch secrets:", err);
    return { success: false, message: msg };
  }
};

/* -------------------------------------------------------------------------- */
/*                                UPDATE PIN                                  */
/* -------------------------------------------------------------------------- */

/**
 * Update the card PIN securely.
 *
 * - Encrypts provided PIN using AES-GCM & session secret key.
 * - Calls backend API to update PIN.
 *
 * @param pin - User-entered plain-text PIN.
 * @param secretKey - Session AES key (hex).
 * @returns `{ success, message }`
 */
export const handleUpdatePin = async ({
  pin,
  secretKey,
  sessionId,
}: {
  pin: string;
  secretKey: string;
  sessionId: string;
}): Promise<{ success: boolean; message: string }> => {
  try {
    console.log("[handleUpdatePin] Encrypting and updating PIN...");

    const { userCard } = useCardStatusStore.getState();

    if (!userCard?.cardID) {
      console.warn("[handleUpdatePin] Missing card ID.");
      return {
        success: false,
        message: "Card ID is missing.",
      };
    }

    const { iv, data } = encryptPinWithSessionKey(pin, secretKey);
    const { success, message } = await updatePin(
      iv,
      data,
      sessionId,
      userCard.cardID
    );

    if (success) console.log("[handleUpdatePin] PIN updated successfully.");
    else console.warn("[handleUpdatePin] PIN update failed:", message);

    return { success, message: message || "" };
  } catch (err) {
    const msg =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error("[handleUpdatePin] Error updating PIN:", err);
    return { success: false, message: msg };
  }
};

/* -------------------------------------------------------------------------- */
/*                               SHOW CARD LIMIT                              */
/* -------------------------------------------------------------------------- */

/**
 * Retrieve the current card transaction limit.
 *
 * @returns `{ success, message, limits? }`
 */
export const handleShowCardLimit = async (): Promise<{
  success: boolean;
  message: string;
  limits?: {
    amount: number; // in cents
    frequency: CardLimitFrequency;
  };
}> => {
  try {
    console.log("[handleShowCardLimit] Fetching card limit...");

    const { userCard } = useCardStatusStore.getState();

    if (!userCard) {
      console.warn("[handleShowCardLimit] Missing card data.");
      return {
        success: false,
        message: "Card information is missing.",
      };
    }

    const { cardID } = userCard;
    const { amount, frequency } = await getCardLimit(cardID);

    console.log("[handleShowCardLimit] Limit retrieved successfully.");
    return {
      success: true,
      message: "Card limit fetched successfully.",
      limits: { amount, frequency },
    };
  } catch (err) {
    const msg =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error("[handleShowCardLimit] Error fetching limit:", err);
    return { success: false, message: msg };
  }
};

/* -------------------------------------------------------------------------- */
/*                               UPDATE LIMIT                                 */
/* -------------------------------------------------------------------------- */

/**
 * Update the transaction limit for a card.
 *
 * - Converts limit to cents.
 * - Calls backend API to update.
 *
 * @param limit - New limit as decimal string (e.g. `"100.00"`).
 * @returns `{ success, message }`
 */
export const handleUpdateLimit = async ({
  limit,
}: {
  limit: string;
}): Promise<{ success: boolean; message: string }> => {
  try {
    console.log("[handleUpdateLimit] Updating card transaction limit...");

    const { userCard } = useCardStatusStore.getState();

    if (!userCard?.cardID) {
      console.warn("[handleUpdateLimit] Missing card ID.");
      return {
        success: false,
        message: "Card ID is missing.",
      };
    }

    const formattedLimit = Math.round(parseFloat(limit) * 100); // Convert to cents
    const { success, message } = await updateLimit(
      formattedLimit,
      userCard?.cardID
    );

    if (success) console.log("[handleUpdateLimit] Limit updated successfully.");
    else console.warn("[handleUpdateLimit] Limit update failed:", message);

    return { success, message: message || "" };
  } catch (err) {
    const msg =
      err instanceof Error ? err.message : "An unexpected error occurred.";
    console.error("[handleUpdateLimit] Error updating limit:", err);
    return { success: false, message: msg };
  }
};
